import { useState } from 'react';

import { CalendarRange, FolderKanban, ListChecks, Users } from 'lucide-react';

import {
  FILTER_OPTION_NO_VALUE,
  TableFilterConfig,
  TableFilterOption,
} from '@/components/data-table/filters-v2';
import i18n from '@/core/i18n';
import { getShortClients } from '@/modules/clients';
import {
  AvatarName,
  EntityAvatar,
  getAssignmentStatusOptions,
  getProjectStatusOptions,
} from '@/modules/common';
import { getMemberStatusText } from '@/modules/common/components/molecules/badge/status-configs/team-member-status-config';
import { mapMemberStatusFromDTO } from '@/modules/common/mappers/member-status.mapper';
import { CustomAttribute } from '@/modules/common/types/custom-attribute';
import { MemberStatusDTO } from '@/modules/common/types/dto/member-status.dto';
import { TeamMemberStatusDTO } from '@/modules/common/types/team-member-status-dto';
import { buildCustomAttributeFilterConfigsV2 } from '@/modules/common/utils/custom-attribute-filters';
import { getShortProjects } from '@/modules/projects';
import { renderProjectFilterLabelWithAvatar } from '@/modules/projects/helpers/project-dropdown-option';
import { ProjectStatus } from '@/modules/projects/types/project';
import { getShortTeamMembers } from '@/modules/team';
import {
  loadCitiesForFilter,
  loadCountriesForFilter,
  loadDepartmentsForFilter,
  loadRolesForFilter,
  loadSenioritiesForFilter,
} from '@/modules/team/api/team-filter-options.api';
import { MembershipType } from '@/modules/team/types/membership-type';
import { RangeMode } from '@/types/filters';

import { ScrollableText } from '../components/assignment/text/scrollable-text';
import {
  PLANNER_ASSIGNMENT_PROBABILITY_FILTER_KEY,
  PLANNER_AVAILABILITY_FILTER_KEY,
  PLANNER_BILLABLE_AVAILABILITY_FILTER_KEY,
  PLANNER_DATE_RANGE_FILTER_KEY,
  PLANNER_IS_MANAGER_FILTER_KEY,
  PLANNER_MEMBER_MANAGER_FILTER_KEY,
  PLANNER_PROJECT_MANAGER_FILTER_KEY,
  PLANNER_PROJECT_PROBABILITY_FILTER_KEY,
  PLANNER_PROJECT_STATUS_FILTER_KEY,
} from '../constants';
import { AssignmentStatus } from '../types/assignment-status';

const MEMBER_FILTER_ANIMATION_DELAY_MS = 100;

// Shared More/Less/Equal options for every rangeMode filter. Built lazily (not a
// module constant) so i18n.t runs at config-build time, after translations load.
const getRangeModeOptions = () => [
  { label: i18n.t('authenticated.planner:filters.moreThan'), value: RangeMode.More },
  { label: i18n.t('authenticated.planner:filters.lessThan'), value: RangeMode.Less },
  { label: i18n.t('authenticated.planner:filters.equalTo'), value: RangeMode.Equal },
];

// Section group keys used to bucket the planner filters in the dropdown.
// Resolved to translations at call time (module-level i18n.t would run before i18n loads).
const FILTER_SECTION_TEAM = 'authenticated.planner:filters.sections.team';
const FILTER_SECTION_PROJECT = 'authenticated.planner:filters.sections.project';
const FILTER_SECTION_ASSIGNMENT = 'authenticated.planner:filters.sections.assignment';
const FILTER_SECTION_GENERAL = 'authenticated.planner:filters.sections.general';

function MemberFilterOption({ value, option }: { value: string; option: TableFilterOption }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className="flex w-full min-w-0 items-center gap-2 overflow-hidden"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <EntityAvatar name={value} size="sm" image={option.avatar} />
      <div className="flex min-w-0 flex-1 flex-col items-start gap-0.5 overflow-hidden">
        <ScrollableText
          className="w-full text-sm"
          externalHovered={hovered}
          animationDelayMs={MEMBER_FILTER_ANIMATION_DELAY_MS}
        >
          {value}
        </ScrollableText>
        {option.status && (
          <span className="truncate text-xs text-muted-foreground">
            {getMemberStatusText(mapMemberStatusFromDTO(option.status as MemberStatusDTO))}
          </span>
        )}
      </div>
    </div>
  );
}

export const getPlannerFiltersConfig = (
  customAttributes?: CustomAttribute[],
  options?: {
    /** Whether the filter date range is set — gates the Availability filter */
    hasDateRangeFilter?: boolean;
    /** Whether projectStatus includes TENTATIVE — gates the Probability filter */
    hasTentativeProjectStatus?: boolean;
  }
): TableFilterConfig[] => [
  {
    // Scopes ALL other filters to an explicit range instead of the loaded
    // viewport; assignments outside it come back isFilteredOut (OPS-1158).
    type: 'dateRange',
    name: PLANNER_DATE_RANGE_FILTER_KEY,
    group: i18n.t(FILTER_SECTION_GENERAL),
    groupIcon: <CalendarRange />,
    label: i18n.t('authenticated.planner:filters.dateRange'),
  },
  {
    type: 'dynamic-autocomplete',
    name: 'user',
    group: i18n.t(FILTER_SECTION_TEAM),
    groupIcon: <Users />,
    withSearch: true,
    label: i18n.t('authenticated.planner:filters.userName'),
    loadOptions: params => {
      return getShortTeamMembers({
        filter: {
          searchString: params.searchString,
        },
        pagination: { cursor: params.cursor, pageSize: params.pageSize },
      });
    },
    renderLabel: (value: string, option) => <MemberFilterOption value={value} option={option} />,
  },
  {
    type: 'autocomplete',
    name: 'memberStatus',
    group: i18n.t(FILTER_SECTION_TEAM),
    label: i18n.t('authenticated.planner:filters.memberStatus'),
    options: [
      {
        value: TeamMemberStatusDTO.ACTIVE,
        label: i18n.t('authenticated.planner:filters.memberStatuses.active'),
      },
      {
        value: TeamMemberStatusDTO.BASIC,
        label: i18n.t('authenticated.planner:filters.memberStatuses.basic'),
      },
      {
        value: TeamMemberStatusDTO.INVITED,
        label: i18n.t('authenticated.planner:filters.memberStatuses.invited'),
      },
      {
        value: TeamMemberStatusDTO.INVITE_EXPIRED,
        label: i18n.t('authenticated.planner:filters.memberStatuses.inviteExpired'),
      },
      {
        value: TeamMemberStatusDTO.PLACEHOLDER,
        label: i18n.t('authenticated.planner:filters.memberStatuses.placeholder'),
      },
      {
        value: TeamMemberStatusDTO.ARCHIVED,
        label: i18n.t('authenticated.planner:filters.memberStatuses.archived'),
      },
    ],
  },
  {
    // Manager flag multiselect: both/neither selected = all members
    type: 'autocomplete',
    name: PLANNER_IS_MANAGER_FILTER_KEY,
    group: i18n.t(FILTER_SECTION_TEAM),
    label: i18n.t('authenticated.planner:filters.isManager'),
    options: [
      {
        value: 'true',
        label: i18n.t('authenticated.planner:filters.managerOptions.manager'),
      },
      {
        value: 'false',
        label: i18n.t('authenticated.planner:filters.managerOptions.notManager'),
      },
    ],
  },
  {
    type: 'autocomplete',
    name: 'membershipType',
    group: i18n.t(FILTER_SECTION_TEAM),
    label: i18n.t('authenticated.planner:filters.membershipType'),
    options: [
      {
        value: MembershipType.InHouse,
        label: i18n.t('authenticated.planner:filters.membershipTypes.inHouse'),
      },
      {
        value: MembershipType.Partner,
        label: i18n.t('authenticated.planner:filters.membershipTypes.partner'),
      },
      {
        value: MembershipType.Contractor,
        label: i18n.t('authenticated.planner:filters.membershipTypes.contractor'),
      },
    ],
  },
  {
    type: 'dynamic-autocomplete',
    name: 'department',
    group: i18n.t(FILTER_SECTION_TEAM),
    withSearch: true,
    label: i18n.t('authenticated.planner:filters.department'),
    loadOptions: loadDepartmentsForFilter,
    topOptions: [
      {
        value: FILTER_OPTION_NO_VALUE,
        label: i18n.t('authenticated.planner:filters.noDepartment'),
      },
    ],
  },
  {
    type: 'dynamic-autocomplete',
    name: 'role',
    group: i18n.t(FILTER_SECTION_TEAM),
    withSearch: true,
    label: i18n.t('authenticated.planner:filters.role'),
    loadOptions: loadRolesForFilter,
    topOptions: [
      { value: FILTER_OPTION_NO_VALUE, label: i18n.t('authenticated.planner:filters.noRole') },
    ],
  },
  {
    type: 'dynamic-autocomplete',
    name: 'seniority',
    group: i18n.t(FILTER_SECTION_TEAM),
    withSearch: true,
    label: i18n.t('authenticated.planner:filters.seniority'),
    loadOptions: loadSenioritiesForFilter,
    topOptions: [
      {
        value: FILTER_OPTION_NO_VALUE,
        label: i18n.t('authenticated.planner:filters.noSeniority'),
      },
    ],
  },
  {
    // Filter members by their assigned manager — options are members who are managers
    type: 'dynamic-autocomplete',
    name: PLANNER_MEMBER_MANAGER_FILTER_KEY,
    group: i18n.t(FILTER_SECTION_TEAM),
    withSearch: true,
    label: i18n.t('authenticated.planner:filters.memberManager'),
    loadOptions: params =>
      getShortTeamMembers({
        filter: { searchString: params.searchString, isManager: true },
        pagination: { cursor: params.cursor, pageSize: params.pageSize },
      }),
    renderLabel: (value: string, option) => <MemberFilterOption value={value} option={option} />,
    topOptions: [
      { value: FILTER_OPTION_NO_VALUE, label: i18n.t('authenticated.planner:filters.noManager') },
    ],
  },
  {
    type: 'dynamic-autocomplete',
    name: 'country',
    group: i18n.t(FILTER_SECTION_TEAM),
    withSearch: true,
    label: i18n.t('authenticated.planner:filters.country'),
    loadOptions: loadCountriesForFilter,
    topOptions: [
      { value: FILTER_OPTION_NO_VALUE, label: i18n.t('authenticated.planner:filters.noCountry') },
    ],
  },
  {
    type: 'dynamic-autocomplete',
    name: 'city',
    group: i18n.t(FILTER_SECTION_TEAM),
    withSearch: true,
    label: i18n.t('authenticated.planner:filters.city'),
    loadOptions: loadCitiesForFilter,
    dependsOn: 'country',
    topOptions: [
      { value: FILTER_OPTION_NO_VALUE, label: i18n.t('authenticated.planner:filters.noCity') },
    ],
  },
  {
    type: 'rangeMode',
    name: 'capacity',
    group: i18n.t(FILTER_SECTION_TEAM),
    label: i18n.t('authenticated.planner:filters.capacity'),
    placeholder: i18n.t('authenticated.planner:filters.exampleCapacity'),
    modes: getRangeModeOptions(),
    unit: i18n.t('authenticated.planner:filters.unit.hDay'),
    inclusiveToggle: true,
  },
  {
    type: 'rangeMode',
    name: 'billableCapacity',
    group: i18n.t(FILTER_SECTION_TEAM),
    label: i18n.t('authenticated.planner:filters.billableCapacity'),
    placeholder: i18n.t('authenticated.planner:filters.exampleCapacity'),
    modes: getRangeModeOptions(),
    unit: i18n.t('authenticated.planner:filters.unit.hDay'),
    inclusiveToggle: true,
  },
  {
    // Average free (unassigned) h/day over the filter date range — requires the
    // date range filter (backend rejects it otherwise, code 2011), hence the gate.
    type: 'rangeMode',
    name: PLANNER_AVAILABILITY_FILTER_KEY,
    group: i18n.t(FILTER_SECTION_TEAM),
    label: i18n.t('authenticated.planner:filters.availability'),
    placeholder: i18n.t('authenticated.planner:filters.exampleCapacity'),
    modes: getRangeModeOptions(),
    unit: i18n.t('authenticated.planner:filters.unit.hDay'),
    inclusiveToggle: true,
    disabled: !options?.hasDateRangeFilter,
    disabledHint: i18n.t('authenticated.planner:filters.availabilityRequiresDateRange'),
  },
  {
    // Same as Availability but measured against billable capacity with billable
    // assignments only (mirrors the Capacity / Billable capacity pairing).
    type: 'rangeMode',
    name: PLANNER_BILLABLE_AVAILABILITY_FILTER_KEY,
    group: i18n.t(FILTER_SECTION_TEAM),
    label: i18n.t('authenticated.planner:filters.billableAvailability'),
    placeholder: i18n.t('authenticated.planner:filters.exampleCapacity'),
    modes: getRangeModeOptions(),
    unit: i18n.t('authenticated.planner:filters.unit.hDay'),
    inclusiveToggle: true,
    disabled: !options?.hasDateRangeFilter,
    disabledHint: i18n.t('authenticated.planner:filters.availabilityRequiresDateRange'),
  },
  ...(customAttributes
    ? buildCustomAttributeFilterConfigsV2(customAttributes, 10, {
        group: i18n.t(FILTER_SECTION_TEAM),
        noValueOptionLabel: i18n.t('authenticated.planner:filters.noCustomAttributeValue'),
      })
    : []),
  {
    type: 'dynamic-autocomplete',
    name: 'projectName',
    group: i18n.t(FILTER_SECTION_PROJECT),
    groupIcon: <FolderKanban />,
    withSearch: true,
    label: i18n.t('authenticated.planner:filters.projectName'),
    loadOptions: params =>
      getShortProjects({
        pagination: { cursor: params.cursor, pageSize: params.pageSize },
        filter: { searchString: params.searchString },
      }),
    renderLabel: renderProjectFilterLabelWithAvatar,
  },
  {
    // OPS-1493: filter planner data by project status
    type: 'autocomplete',
    name: PLANNER_PROJECT_STATUS_FILTER_KEY,
    group: i18n.t(FILTER_SECTION_PROJECT),
    displayDot: true,
    label: i18n.t('authenticated.planner:filters.projectStatus'),
    options: getProjectStatusOptions([
      ProjectStatus.Active,
      ProjectStatus.Tentative,
      ProjectStatus.Archived,
    ]),
  },
  {
    // Probability only exists on tentative projects — locked until the project
    // status filter includes TENTATIVE (mirrors the availability/date-range gate).
    type: 'rangeMode',
    name: PLANNER_PROJECT_PROBABILITY_FILTER_KEY,
    group: i18n.t(FILTER_SECTION_PROJECT),
    label: i18n.t('authenticated.planner:filters.projectProbability'),
    placeholder: i18n.t('authenticated.planner:filters.exampleProbability'),
    modes: getRangeModeOptions(),
    unit: i18n.t('authenticated.planner:filters.unit.percent'),
    inclusiveToggle: true,
    disabled: !options?.hasTentativeProjectStatus,
    disabledHint: i18n.t('authenticated.planner:filters.probabilityRequiresTentative'),
  },
  {
    type: 'dynamic-autocomplete',
    name: 'client',
    group: i18n.t(FILTER_SECTION_PROJECT),
    withSearch: true,
    label: i18n.t('authenticated.planner:filters.clientName'),
    loadOptions: params =>
      getShortClients({
        pagination: { cursor: params.cursor, pageSize: params.pageSize },
        filter: { searchString: params.searchString },
      }),
    renderLabel: (value: string) => <AvatarName name={value} />,
    topOptions: [
      { value: FILTER_OPTION_NO_VALUE, label: i18n.t('authenticated.planner:filters.noClient') },
    ],
  },
  {
    // Filter projects by their assigned manager(s) — options are members who are managers
    type: 'dynamic-autocomplete',
    name: PLANNER_PROJECT_MANAGER_FILTER_KEY,
    group: i18n.t(FILTER_SECTION_PROJECT),
    withSearch: true,
    label: i18n.t('authenticated.planner:filters.projectManager'),
    loadOptions: params =>
      getShortTeamMembers({
        filter: { searchString: params.searchString, isManager: true },
        pagination: { cursor: params.cursor, pageSize: params.pageSize },
      }),
    renderLabel: (value: string, option) => <MemberFilterOption value={value} option={option} />,
    topOptions: [
      { value: FILTER_OPTION_NO_VALUE, label: i18n.t('authenticated.planner:filters.noManager') },
    ],
  },
  {
    type: 'autocomplete',
    name: 'status',
    group: i18n.t(FILTER_SECTION_ASSIGNMENT),
    groupIcon: <ListChecks />,
    displayDot: true,
    label: i18n.t('authenticated.planner:status'),
    options: getAssignmentStatusOptions([
      AssignmentStatus.Confirmed,
      AssignmentStatus.Draft,
      AssignmentStatus.Unconfirmed,
    ]),
  },
  {
    type: 'rangeMode',
    name: PLANNER_ASSIGNMENT_PROBABILITY_FILTER_KEY,
    group: i18n.t(FILTER_SECTION_ASSIGNMENT),
    label: i18n.t('authenticated.planner:filters.assignmentProbability'),
    placeholder: i18n.t('authenticated.planner:filters.exampleProbability'),
    modes: getRangeModeOptions(),
    unit: i18n.t('authenticated.planner:filters.unit.percent'),
    inclusiveToggle: true,
    min: 1,
    max: 99,
  },
];
